import { useCallback, useEffect, useRef, useState } from 'react';

import { useAppDispatch } from '@/core/store/store';

import { extractMediaFromSources } from '@/features/Reports/helpers';
import { Report } from '@/features/Reports/interfaces';
import {
  resolveReportSourcesMediaViaApi,
  sourceNeedsMediaResolution,
} from '@/features/Reports/media-resolution';
import { refreshReportSources } from '@/features/Reports/store/actions';
import { updateReportSources } from '@/features/Reports/store/slices';

import { ArticlesRefreshStatus } from './ArticlesSection.types';

const SOURCES_AUTO_REFRESH_INTERVAL_MS = 60_000;

type SourcesRefreshState = {
  status: ArticlesRefreshStatus;
  updatedAt: string | null;
  articlesCountDelta: number | null;
  mediaCountDelta: number | null;
};

const initialSourcesRefreshState: SourcesRefreshState = {
  status: 'idle',
  updatedAt: null,
  articlesCountDelta: null,
  mediaCountDelta: null,
};

type RefreshSourcesOptions = {
  skipWhenHidden?: boolean;
};

export const useReportSourcesRefresh = (report: Report) => {
  const dispatch = useAppDispatch();
  const [sourcesRefreshState, setSourcesRefreshState] = useState<SourcesRefreshState>(
    initialSourcesRefreshState
  );
  const isRefreshingRef = useRef(false);
  const isIngestingRef = useRef(false);
  const reportSourceRef = useRef(report.source);

  reportSourceRef.current = report.source;

  const refreshSources = useCallback(
    async (options?: RefreshSourcesOptions) => {
      if (options?.skipWhenHidden && document.hidden) {
        return;
      }

      if (isRefreshingRef.current) {
        return;
      }

      const previousArticlesCount = reportSourceRef.current.length;
      const previousMediaCount = extractMediaFromSources(reportSourceRef.current).length;

      isRefreshingRef.current = true;

      setSourcesRefreshState({
        status: 'loading',
        updatedAt: null,
        articlesCountDelta: null,
        mediaCountDelta: null,
      });

      try {
        const sources = await dispatch(
          refreshReportSources({
            reportId: report.reportId,
            reportSourceType: report.reportSourceType,
          })
        ).unwrap();

        setSourcesRefreshState({
          status: 'success',
          updatedAt: new Date().toISOString(),
          articlesCountDelta: sources.length - previousArticlesCount,
          mediaCountDelta: extractMediaFromSources(sources).length - previousMediaCount,
        });
      } catch {
        setSourcesRefreshState({
          status: 'failure',
          updatedAt: null,
          articlesCountDelta: null,
          mediaCountDelta: null,
        });
      } finally {
        isRefreshingRef.current = false;
      }
    },
    [dispatch, report.reportId, report.reportSourceType]
  );

  useEffect(() => {
    setSourcesRefreshState(initialSourcesRefreshState);
  }, [report.reportId]);

  useEffect(() => {
    const ingestSourcesMedia = async () => {
      if (isIngestingRef.current) {
        return;
      }

      const pendingSources = report.source.filter(sourceNeedsMediaResolution);

      if (!pendingSources.length) {
        return;
      }

      isIngestingRef.current = true;

      try {
        const resolvedSources = await resolveReportSourcesMediaViaApi(report.source);

        dispatch(
          updateReportSources({
            reportId: report.reportId,
            reportSourceType: report.reportSourceType,
            sources: resolvedSources,
          })
        );
      } finally {
        isIngestingRef.current = false;
      }
    };

    void ingestSourcesMedia();
  }, [dispatch, report.reportId, report.reportSourceType, report.source]);

  useEffect(() => {
    void refreshSources();

    const intervalId = setInterval(() => {
      void refreshSources({ skipWhenHidden: true });
    }, SOURCES_AUTO_REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [report.reportId, report.reportSourceType, refreshSources]);

  return {
    refreshStatus: sourcesRefreshState.status,
    refreshUpdatedAt: sourcesRefreshState.updatedAt,
    articlesCountDelta: sourcesRefreshState.articlesCountDelta,
    mediaCountDelta: sourcesRefreshState.mediaCountDelta,
    refreshSources,
  };
};
